/**
 * Page, page size and sort of a listing (Orders or Properties), kept in
 * sessionStorage so leaving the list (the asset workspace, the other list)
 * and coming back lands where the user was (DV-3455).
 *
 * A sibling of the filter bundle (`useListingFilters`), not a field on it:
 * that bundle has its own in-memory owner, and two owners of one key would
 * overwrite each other's writes. Written only when a page is actually
 * fetched, so it always describes what the user last saw.
 */

export type ListingSort = { selector: string; asc: boolean };

export type ListingPaging = {
  page: number;
  pageSize: number;
  sort: ListingSort | null;
};

/** What is stored: the paging plus the orders view it was recorded on. */
export type StoredListingPaging = ListingPaging & { view: string | null };

/** The page sizes the pagination footer offers. */
export const PAGE_SIZES = [25, 50, 100] as const;

export const DEFAULT_PAGING: ListingPaging = { page: 1, pageSize: 25, sort: null };

const isPage = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1;

const isPageSize = (value: unknown): value is number =>
  PAGE_SIZES.includes(value as (typeof PAGE_SIZES)[number]);

const isSort = (value: unknown): value is ListingSort =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as ListingSort).selector === 'string' &&
  typeof (value as ListingSort).asc === 'boolean';

/**
 * The stored paging for `key`, read for the orders view now on screen
 * (`null` for Properties). The page is restored only for the view it was
 * recorded on — page 4 of Completed means nothing to In Progress — while
 * the page size and sort are preferences and carry across views. Each field
 * that is missing or malformed falls back to its default on its own.
 */
export function readListingPaging(key: string, view: string | null): ListingPaging {
  let stored: unknown;
  try {
    stored = JSON.parse(sessionStorage.getItem(key) ?? 'null');
  } catch {
    return DEFAULT_PAGING;
  }
  if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) {
    return DEFAULT_PAGING;
  }

  const { view: storedView, page, pageSize, sort } = stored as Partial<StoredListingPaging>;
  return {
    page: storedView === view && isPage(page) ? page : DEFAULT_PAGING.page,
    pageSize: isPageSize(pageSize) ? pageSize : DEFAULT_PAGING.pageSize,
    sort: isSort(sort) ? { selector: sort.selector, asc: sort.asc } : DEFAULT_PAGING.sort
  };
}

export function writeListingPaging(key: string, paging: StoredListingPaging): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(paging));
  } catch {
    /* quota or serialization error — the list still works, it just won't restore */
  }
}
